import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../api/apiService';
import disLogo from '../assets/DisLogo.png';
import './Login.css';

const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiService.post('/admin-auth/request-otp', {
        mobile_number: phoneNumber,
      });
      
      if (response.data.message) {
        setStep('otp');
        if (response.data.otp) {
          // For development, auto-fill OTP
          setOtp(response.data.otp);
        }
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: any) => d.msg || d).join(', ')
          : err.code === 'ERR_NETWORK' || !err.response
            ? 'Cannot reach server. Is V-Gas-Backend running on http://localhost:8000?'
            : 'Failed to send OTP';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const success = await login(phoneNumber, otp);
    
    if (success) {
      navigate('/dashboard');
    } else {
      setError('Invalid OTP. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-bg-pattern" aria-hidden />
      <div className="login-content">
        <div className="login-brand">
          <img src={disLogo} alt="V-GAS" className="login-logo" />
          <h1 className="login-title">V Gas Admin</h1>
          <p className="login-tagline">Sign in to manage distributors, orders & settings</p>
        </div>
        <div className="login-card">
          <div className="login-steps">
            <span className={`login-step ${step === 'phone' ? 'active' : 'done'}`}>1</span>
            <span className="login-step-line" />
            <span className={`login-step ${step === 'otp' ? 'active' : ''}`}>2</span>
          </div>
          <p className="login-card-label">{step === 'phone' ? 'Enter your phone number' : 'Enter the OTP sent to your phone'}</p>

          {step === 'phone' ? (
            <form onSubmit={handleRequestOTP} className="login-form">
              <div className="form-group">
                <label htmlFor="login-phone">Phone Number</label>
                <input
                  id="login-phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="e.g. 9876543210"
                  required
                  disabled={loading}
                  autoComplete="tel"
                />
              </div>
              {error && <div className="login-error">{error}</div>}
              <button type="submit" disabled={loading} className="login-btn-primary">
                {loading ? 'Sending OTP...' : 'Request OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="login-form">
              <div className="form-group">
                <label htmlFor="login-otp">OTP</label>
                <input
                  id="login-otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  maxLength={6}
                  required
                  disabled={loading}
                  autoComplete="one-time-code"
                />
                {/* <p className="login-hint">Dev OTP: 843157</p> */}
              </div>
              {error && <div className="login-error">{error}</div>}
              <div className="login-button-group">
                <button type="button" onClick={() => setStep('phone')} className="login-btn-secondary" disabled={loading}>
                  Back
                </button>
                <button type="submit" disabled={loading} className="login-btn-primary">
                  {loading ? 'Verifying...' : 'Verify & sign in'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;

